import { getCoreUtils } from "../kReusableCoreTS/kCoreHandler";

/**
 * MAKES A MISSING PHRASE KEY LOUD AT RUNTIME.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * THE RULE THIS ENFORCES (owner ruling, 2026-08-19)
 *
 *   "A wrong caption should create a dead app."
 *   "No softening of the rules - the global error handler is our best tool to reach perfect code."
 *
 * A phrase key that resolves to `undefined` and then gets PAINTED is an error taking the
 * forbidden THIRD fate: not corrected, not reported, just silently shown to a user. Constitution
 * rule 1 allows exactly two fates. This file removes the third one for `LNG.*`.
 *
 * The app entering error mode when a caption cannot resolve is the CORRECT outcome, not a
 * regression. Error mode is the designed signal of a reachable bug; the quiet that follows is
 * the gate doing its job.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * WHY A RUNTIME CHECK IS NEEDED AT ALL, GIVEN THE BUILD GATE EXISTS
 *
 * Product source imports the SHARED all-phrases `kLNG.ts`, so `LNG.ANYKEY` type-checks. The
 * bundler then substitutes the product's SCOPED `kLNG.snapshot.ts` (a resolve rewrite on
 * /[\/\\]kLNG$/ - `Admin\Tools\KBuild\kreplace.mjs`, plus two NormalModuleReplacementPlugin
 * blocks). THE COMPILE-TIME TYPE AND THE RUNTIME VALUE COME FROM TWO DIFFERENT FILES.
 *
 * `Test-kLNGSnapshotCoverage.ps1` closes the STATIC half of that gap and is the first line of
 * defence. It is a text scan, so three things are structurally beyond it, and this file is the
 * only instrument that can see them:
 *
 *   1. A dynamic read - `LNG[SomeVariable]` - which no text extractor can resolve.
 *   2. A product whose build never runs the gate. Measured 2026-08-19: of the 15 registered
 *      products, FIVE (CB, LC, MCD, PE, ICD) have no snapshot on disk at all.
 *   3. A snapshot that is correct on disk but is not the file the bundle actually shipped
 *      (a mis-declared "kLNGSnapshot" entry makes kreplace warn and carry on - see
 *      kreplace.mjs, "Not fatal").
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * WHY ACCESS-TIME (A PROXY) AND NOT A STARTUP SELF-CHECK
 *
 * A startup self-check emitted by the generator would be TAUTOLOGICAL and therefore worthless.
 * The generator builds the snapshot FROM the reference set it just scanned, so "assert every key
 * I referenced is present" is asserting that a set contains itself - it can never fail. The
 * defect is that the snapshot is STALE: the source references keys that were not referenced when
 * the snapshot was last generated, and the snapshot cannot know about those. Only the read
 * itself knows. So the check has to be at access time, and it names the exact key at the exact
 * read, which is what makes the report actionable rather than a mystery blank screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * INIT ORDER - MEASURED, NOT ASSUMED (2026-08-19)
 *
 * The concern to rule out is a `LNG.X` read at MODULE SCOPE, which would run before any error
 * handler is registered and would therefore report into the void. Scanned 1,889 `.ts` files
 * across all 10 products with a snapshot plus both shared libraries:
 *
 *   static field initialisers reading LNG (`static X = LNG.Y`, runs at module eval):  0
 *   unindented lines referencing LNG:                                                 9
 *
 * and all 9 of those are function BODIES (`function F(): string { return LNG.K; }` in
 * FleetChoiceConfigs.ts x8, one arrow in a MOSE test), so the read happens when the function is
 * CALLED, never at module evaluation. THERE IS NO MODULE-SCOPE READ IN THE ESTATE.
 *
 * That matters because `kalmstromShared4.ts:2653` runs `SetCoreUtils(ks)` as the LAST statement
 * of its module body, and `kalmstromShared4.ts:18` imports kLNG. Since every read happens after
 * module-graph evaluation, `getCoreUtils()` returns the real browser handler at every read site
 * that exists today.
 *
 * ⚠ THIS FILE MUST NOT IMPORT `kalmstromShared4`. That would close the cycle
 * kLNG -> kLNGGuard -> kalmstromShared4 -> kLNG and hand one of the three a half-initialised
 * binding. `kCoreHandler.ts` has ZERO imports (verified), so routing through `getCoreUtils()`
 * is the only acyclic way to reach the same handler. The standards checker recognises
 * `getCoreUtils().KGlobalErrorHandler(...)` as equivalent to the `ks.` call - see
 * `IsCoreUtilsAccess()` in `tests/codingStandards.checker.js`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * ⚠ THE RECURSION THIS FILE EXISTS INSIDE, AND WHY THE LATCH IS LOAD-BEARING
 *
 * Reporting a missing key opens the error dialog. The error dialog IS a `kModalDialog`, and
 * `kModalDialog.ts:270` reads `LNG.CLOSE` for its close button's aria-label. On SPColdStorage -
 * the product that has been shipping `aria-label="undefined"` - `LNG.CLOSE` is exactly the key
 * that is missing. So reporting it would read it, which would report it, forever.
 *
 * Two independent brakes, both required:
 *   - `Reporting` re-entrancy latch: a read that happens while a report is in flight is
 *     recorded but does not re-enter the handler.
 *   - per-key dedupe via `MissingKeys`: each key reports at most once per page.
 *
 * The trap still RETURNS `undefined` for that read, so the error dialog renders with a wrong
 * aria-label. That is cosmetic, it is on a dialog whose whole purpose is to announce the bug,
 * and it is strictly better than either recursing or staying silent.
 */
export class kLNGGuard {
    /**
     * Every phrase key that failed to resolve this page, in first-seen order. Append-only and
     * never cleared, so it survives error mode and is readable from an E2E or a unit test after
     * the fact. This is the instrument that makes "prove the loudness fired" possible: the
     * handler dedupes and error mode gags later reports, so the handler alone is not a
     * sufficient record.
     */
    public static MissingKeys: string[] = [];

    /** True while a report is in flight. See the recursion note in the class docblock. */
    private static Reporting: boolean = false;

    /**
     * A phrase key, and nothing else, matches this.
     *
     * DERIVED FROM THE DATA, NOT GUESSED. Measured 2026-08-19 against all 4,876 keys in
     * `phrases.json`: every one matches, and zero of the module-interop / test-framework
     * property probes that get read off an exported class match it -
     * `default`, `__esModule`, `then`, `$$typeof`, `nodeType`, `toJSON`, `constructor`,
     * `caller`, `arguments`, `name`, `length`, `prototype`, `displayName`, `propTypes`,
     * `_isMockFunction`, `asymmetricMatch`, `tagName`, `toString`, `valueOf`,
     * `__webpack_require__`, `SetLanguage`. All are rejected by the second character.
     *
     * ⚠ The second character must allow a DIGIT: A3FormsBuilder's 249 keys are `A3F...`, and a
     * plain two-letter rule silently excused every one of them. The 38 mixed-case KTM keys
     * (`KTMActivity2`) are why the tail is `[A-Za-z0-9_]` rather than uppercase-only.
     *
     * The generator enforces this shape when it emits a snapshot, so the rule cannot go stale
     * behind a new key that does not fit it.
     */
    private static PhraseKeyShape: RegExp = /^[A-Z][A-Z0-9][A-Za-z0-9_]*$/;

    /**
     * Wraps a generated `LNG` class so that reading a key which does not resolve reports to the
     * central handler instead of painting the literal string "undefined".
     *
     * Returns `T`, so `typeof LNG` is unchanged and `LNG.SOMETHING` keeps type-checking against
     * the shared superset exactly as before. That is a hard requirement of this design: the
     * value is wrapped, the TYPE is not touched.
     */
    public static Watch = <T extends object>(Target: T): T => {
        if (getCoreUtils().KErrorMode === true) { return Target; }
        try {
            // Explicitly parameterised. Left to inference, `new Proxy(Target, ...)` widens to
            // Proxy<object> and the method stops returning T - which would silently drop the
            // whole point of this signature, that `typeof LNG` survives the wrap.
            const Handler: ProxyHandler<T> = { get: kLNGGuard.OnGet };
            return new Proxy<T>(Target, Handler);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kLNGGuard.Watch");
            return Target;
        }
    };

    /**
     * The `get` trap. Named property on the class - not an inline handler - per the style rules.
     *
     * DOCUMENTED EXCEPTION - the error-mode guard is deliberately NOT the first statement.
     * It sits below, after the value pass-through and immediately before the report, and the
     * placement is load-bearing rather than an oversight: a top-of-method guard returning a safe
     * default would blank EVERY caption in the error dialog that the handler is at that moment
     * trying to show. This is the same reason kModalDialog uses a compound guard on its own
     * dialog infrastructure. `tests\kLNGGuard.test.js` pins the behaviour - "once error mode is
     * engaged the trap still returns values so the error dialog can render" - so a future
     * "correction" that hoists the guard fails a test rather than silently blanking the one
     * dialog whose job is to announce the bug. The try/catch and the KGlobalErrorHandler call
     * are both present and are not covered by this exception.
     */
    private static OnGet = (Target: object, Key: PropertyKey, Receiver: unknown): unknown => {
        try {
            const Value: unknown = Reflect.get(Target, Key, Receiver);
            if (Value !== undefined) { return Value; }
            if (typeof Key !== "string") { return Value; }
            if (kLNGGuard.PhraseKeyShape.test(Key) === false) { return Value; }
            // ERROR-MODE GUARD, deliberately placed AFTER the pass-through and BEFORE the report,
            // not at the top of the method. A top guard that returned a safe default would blank
            // every caption in the error dialog the handler is trying to show - the same reason
            // kModalDialog uses a compound guard. Once error mode is engaged the app is already
            // dead and already reported, so the correct behaviour is: still serve values, stop
            // reporting.
            if (getCoreUtils().KErrorMode === true) { return Value; }
            kLNGGuard.Report(Key);
            return Value;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kLNGGuard.OnGet");
            return undefined;
        }
    };

    /**
     * Routes one unresolved key to the central handler, once.
     *
     * There is no fallback and no degraded caption on purpose. The handler flips `KErrorMode`,
     * every guarded method in the page then returns its safe default, and the app stops. That is
     * the intended outcome: a build shipped a caption it cannot render, and the only thing that
     * gets that fixed is a report somebody has to act on.
     */
    private static Report = (Key: string): void => {
        // Unlike OnGet, this one CAN take the ordinary top-of-method guard, and does. It is
        // reached only from OnGet, which has already decided that a read in error mode is served
        // but not reported - so this guard never fires today. It is here because the standard is
        // absolute and because the two decisions must not be allowed to drift apart: if OnGet's
        // gate is ever moved, this method must still refuse to reach the handler rather than
        // start reporting into an app that is already dead and already reported.
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (kLNGGuard.MissingKeys.indexOf(Key) >= 0) { return; }
            kLNGGuard.MissingKeys.push(Key);
            if (kLNGGuard.Reporting === true) { return; }
            kLNGGuard.Reporting = true;
            const Missing: Error = new Error(
                "The phrase key LNG." + Key + " did not resolve at runtime, so this build would " +
                "have painted the literal string \"undefined\" on screen. The key exists in " +
                "phrases.json and in the shared kLNG.ts (which is why the type check passed), but " +
                "it is absent from the kLNG.snapshot.ts this product bundles - the snapshot is " +
                "stale relative to the code. Repair: run the product's LNG build to regenerate " +
                "kLNG.snapshot.ts, then verify with " +
                "X:\\Source\\Admin\\ReUsablePS\\LNG\\Test-kLNGSnapshotCoverage.ps1 -Mode Estate.");
            Missing.name = "kLNGMissingPhrase";
            // The key is in the LOCATION as well as the message: the location is what the handler
            // stamps into the report title, and "which key" is the entire actionable content.
            getCoreUtils().KGlobalErrorHandler(Missing, "kLNGGuard.Missing." + Key);
            kLNGGuard.Reporting = false;
        } catch (e) {
            kLNGGuard.Reporting = false;
            getCoreUtils().KGlobalErrorHandler(e, "kLNGGuard.Report");
        }
    };
}
